import { AsyncPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DOCUMENT, ViewEncapsulation, inject, signal } from '@angular/core';
import { Attachment, DeleteAdditionalAttachmentRequest, SaveAdditionFileRequest } from '@jms/model';
import { AttachmentFieldAllowedTypes, AttachmentFieldUploadMode, AttachmentModule } from '@jms/ui/ui.attachment';
import { FileUtility, FileViewModule } from '@jms/ui/ui.file.view';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';
import { ResizeImagePipe, SafeUrlPipe } from '@jms/utils/util.pipes';
import { StateComponent } from '@jms/utils/util.state.component';
import { PdfBlobPipe } from '@workbuddy/ui/pipes';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';
import { saveAs } from 'file-saver';
import * as Enumerable from 'linq';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { injectDialogData, injectDialogRef } from '@workbuddy/ui/v2/blocks/dialog';
import { ButtonComponent } from '@workbuddy/ui/components/button';
import { Observable, lastValueFrom, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { ItemMediaService } from '../../services/item.media.service';

export interface ItemMediaDialogData {
  itemId: string;
  itemName: string;
  stageId: string;
  /** False renders the dialog read-only: existing files stay viewable, upload and delete do not. */
  canManageMedia: boolean;
}

interface ItemMediaVM {
  attachments: Attachment[];
  preview: Attachment;
  readonly: boolean;
}

interface PreviewOption {
  model?: { value: string; };
  minimap?: { enabled: boolean; };
  readonly?: boolean;
  path: string;
  fileExtension: 'pdf' | 'csv';
  isLoading?: boolean;
}

const initialState = { attachments: [], preview: null, readonly: false } as ItemMediaVM;

/**
 * Item attachments dialog, ported from the legacy items tab's `embedded-code-media`.
 *
 * Kept deliberately close to the original — same state container, same preview branching, same
 * upload diffing — because this is a port and every modernisation here is a chance to change
 * behaviour that nobody would notice until a technician's photo stopped opening. The legacy
 * component is marked deprecated and goes away with its tab; refactor to signals then, with tests.
 */
@Component({
  selector: 'item-media-dialog',
  standalone: true,
  imports: [AsyncPipe, AttachmentModule, FileViewModule, PdfJsViewerModule, MonacoEditorModule, SafeUrlPipe, ResizeImagePipe, PdfBlobPipe, ButtonComponent],
  providers: [ItemMediaService],
  templateUrl: './item.media.dialog.component.html',
  styleUrls: ['./item.media.dialog.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemMediaDialogComponent extends StateComponent<ItemMediaVM> {

  private readonly http = inject(HttpClient);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly confirmService = inject(ConfirmServicePro);
  private readonly mediaService = inject(ItemMediaService);
  private readonly dialogRef = injectDialogRef<boolean, ItemMediaDialogComponent>();
  private readonly data = injectDialogData<ItemMediaDialogData>();
  private readonly document = inject(DOCUMENT);

  public imgPath: string;
  public filePath: string;
  public fileTitle: string;

  public mode = AttachmentFieldUploadMode.Mutliple;
  public type = AttachmentFieldAllowedTypes.All;

  public allowedExtensionFile = ['doc', 'docx', 'pdf', 'xlsm', 'xlsx', 'xls', 'csv', 'txt'];
  public allowedExtensionIMG = ['jpg', 'jpeg', 'jpe', 'jfif', 'png', 'PNG', 'webp'];

  protected $previewOption = signal<PreviewOption>(null);

  /** Compared on close: the row shows an attachment count, so only a change needs a board refetch. */
  private oldCount = 0;

  public constructor() {

    super(initialState);

    this.setState({ readonly: !this.data?.canManageMedia });
    this.load();
  }

  public closePreview(): void {

    const currentCount = this.vm.attachments?.length ?? 0;
    this.dialogRef.close(this.oldCount !== currentCount);
  }

  public async onChangeAttachments(attachments: Attachment[]): Promise<void> {

    if (!attachments?.length) return;

    // The field re-emits everything it holds, so only what is not already saved is new.
    attachments = Enumerable.from(attachments).except(this.vm.attachments, x => x._id).toArray();

    const request = {
      name: this.data?.itemName,
      attachments: attachments,
      codeId: this.data?.itemId,
      _id: null,
      tags: [],
      workOrderId: this.data?.stageId,
    } as SaveAdditionFileRequest;

    const result = await lastValueFrom(this.mediaService.save(request));

    if (!result) return;

    this.setState({ attachments: result?.attachments });
    this.preview(Enumerable.from(result?.attachments).lastOrDefault());
  }

  public preview(attachment: Attachment): void {

    this.setState({ preview: attachment });

    if (!attachment) {
      this.imgPath = null;
      this.fileTitle = null;
      this.filePath = null;
      this.$previewOption.set(null);
      return;
    }

    if (FileUtility.getFileType(attachment.path, this.allowedExtensionFile, this.allowedExtensionIMG) === 'Image') {
      this.imgPath = attachment.path;
      this.fileTitle = attachment.title;
      this.filePath = null;
      this.$previewOption.set(null);
      return;
    }

    this.filePath = attachment.path;
    this.fileTitle = attachment.title;
    this.imgPath = null;

    switch (this.getExtension(this.filePath)) {

      case 'pdf':
        this.$previewOption.set({ fileExtension: 'pdf', path: this.filePath });
        break;

      // Monaco needs the text, not a URL, so the blob is read before the editor is shown.
      case 'csv':
        this.$previewOption.set({ isLoading: true, path: this.filePath, fileExtension: 'csv' });

        this.loadCode(this.filePath)
          .pipe(
            take(1),
            map(code => this.$previewOption.set({ model: { value: code }, minimap: { enabled: false }, readonly: true, path: this.filePath, fileExtension: 'csv' })),
          )
          .subscribe();
        break;

      default:
        this.$previewOption.set(null);
    }
  }

  public prepareLinkPreviewForm(path: string): string {

    if (path?.includes('http')) return this.prepareLinkPreview(path);

    return this.document.location.origin + '/forms/' + path;
  }

  public prepareLinkPreview(path: string): string {

    const extension = this.getExtension(path);
    const isOkWithOfficeWeb = ['ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx', 'xlsm'].some(item => item === extension);

    const linkHost = isOkWithOfficeWeb
      ? 'https://view.officeapps.live.com/op/view.aspx?src='
      : `https://pdf-viewer.${ConfigService.environment.domain}/?src=`;

    return linkHost + path + (isOkWithOfficeWeb ? '' : '&filename=' + this.fileTitle);
  }

  public downloadFile(file: Attachment): void {

    this.http
      .get(file?.path, { responseType: 'blob' })
      .pipe(
        map(blob => saveAs(blob as Blob, file?.title ?? 'file')),
        catchError(() => of(null)),
      )
      .subscribe();
  }

  public async deleteFile(file: Attachment): Promise<void> {

    const confirmed = await this.confirmService.confirm('Delete Attachment', 'Are you sure wish to delete this file?', 'danger');

    if (!confirmed) return;

    const request = {
      _id: this.mediaService.getMediaId(this.data?.stageId, this.data?.itemId),
      attachment: file,
      codeId: this.data?.itemId,
      workOrderId: this.data?.stageId,
    } as DeleteAdditionalAttachmentRequest;

    const result = await lastValueFrom(this.mediaService.delete(request));

    // The service converts an HTTP failure to null after showing the toast. Keep the server-backed
    // attachment in the dialog in that case; removing it locally makes a failed delete look successful.
    if (!result?.success) return;

    const attachments = Enumerable.from(this.vm.attachments).where(x => x._id !== file?._id).toArray();

    this.setState({ attachments });
    this.preview(Enumerable.from(attachments).firstOrDefault());
    this.cdr.detectChanges();
  }

  private load(): void {

    if (!this.data?.itemId) return;

    this.mediaService
      .getMedia(this.mediaService.getMediaId(this.data?.stageId, this.data?.itemId))
      .pipe(take(1))
      .subscribe(attachments => {

        this.setState({ attachments: attachments ?? [] });
        this.oldCount = attachments?.length ?? 0;
        this.preview(Enumerable.from(attachments ?? []).firstOrDefault());
      });
  }

  private getExtension(path: string): string {

    if (!path) return '';

    const components = path.split('.');
    return !components.length ? '' : components[components.length - 1];
  }

  private loadCode(path: string): Observable<string> {

    return this.http
      .get(path, { responseType: 'blob' })
      .pipe(
        switchMap(blob => new Promise<string>(resolve => {

          const fileReader = new FileReader();
          fileReader.readAsText(blob);
          fileReader.onload = () => resolve(fileReader.result as string);
        })),
        catchError(() => of(null)),
      );
  }
}
